import Icon from '../../../components/AppIcon';

const SummaryPanel = ({ summaryData }) => {
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    })?.format(amount);
  };

  const formatTonnage = (tonnage) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(tonnage);
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-6">
      <div className="flex items-center gap-2">
        <Icon name="BarChart3" size={20} color="#3B82F6" />
        <h3 className="text-lg font-semibold text-slate-900">
          Period Summary
        </h3>
      </div>
      <div className="space-y-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
          <p className="text-xs text-blue-700 mb-1">Total Revenue</p>
          <p className="text-3xl font-bold text-blue-900">
            {formatCurrency(summaryData?.totalRevenue)}
          </p>
          <div className="flex items-center gap-1 mt-2">
            <Icon name="TrendingUp" size={16} color="#059669" />
            <span className="text-xs text-green-600 font-medium">
              +12.5% vs last period
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-600 mb-1">Total Tonnage</p>
            <p className="text-xl font-bold text-slate-900">
              {formatTonnage(summaryData?.totalTonnage)}
            </p>
            <p className="text-xs text-slate-600 mt-1">MT</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-600 mb-1">Avg Fee Rate</p>
            <p className="text-xl font-bold text-slate-900">
              {formatCurrency(summaryData?.avgFeeRate)}
            </p>
            <p className="text-xs text-slate-600 mt-1">/MT</p>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-semibold text-slate-900">
            Financial Breakdown
          </h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Gross Fees</span>
              <span className="text-sm font-semibold text-slate-900">
                {formatCurrency(summaryData?.grossFees)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Advance Paid</span>
              <span className="text-sm font-semibold text-green-600">
                {formatCurrency(summaryData?.advancePaid)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Reimbursements</span>
              <span className="text-sm font-semibold text-red-600">
                {formatCurrency(summaryData?.reimbursements)}
              </span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-200">
              <span className="text-sm font-semibold text-slate-900">
                Net Balance
              </span>
              <span className="text-sm font-bold text-blue-600">
                {formatCurrency(summaryData?.netBalance)}
              </span>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">
            Top Suppliers by Volume
          </h4>
          <div className="space-y-3">
            {summaryData?.topSuppliers?.map((supplier, index) => (
              <div key={index} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">
                    {supplier?.name}
                  </span>
                  <span className="text-sm font-semibold text-slate-900">
                    {formatTonnage(supplier?.tonnage)} MT
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${supplier?.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} color="#D97706" />
            <div>
              <p className="text-sm font-medium text-yellow-900 mb-1">
                Pending Actions
              </p>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• 3 invoices pending generation</li>
                <li>• 1 payment overdue by 15 days</li>
                <li>• 2 rate changes effective next month</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">
            Integration Status
          </h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm text-slate-700">
                  Shipment Register
                </span>
              </div>
              <span className="text-xs text-green-600">Synced</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm text-slate-700">Cost Register</span>
              </div>
              <span className="text-xs text-green-600">Synced</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500" />
                <span className="text-sm text-slate-700">ERP System</span>
              </div>
              <span className="text-xs text-yellow-600">Pending</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryPanel;